import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
  useNavigate,
} from "react-router-dom";
import { AuthContext } from "../../../../../context/AuthContext";
import Navbar from "../../../../ui/Navbar";
import SubmittedAssessments from "./SubmittedAssessments";
import PendingAssessments from "./PendingAssessments";
import Loader from "../../../../ui/Loader";
import { fetchWithAuth } from "../../../../../utils/api";

export default function TestGradingHome() {
  const { isAuthenticated, primaryRole, user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { testId } = useParams();
  const [classInfo, setClassInfo] = useState({
    id: null,
    name: "Loading...",
    instructorName: "",
    level: "",
  });
  const [instituteInfo, setInstituteInfo] = useState(null);
  const [loadingInstitute, setLoadingInstitute] = useState(true);
  const [testInfo, setTestInfo] = useState({ id: null, name: "Loading..." });
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submittedCount, setSubmittedCount] = useState(null);
  const hasRedirectedRef = useRef(false);
  const serverUrl = process.env.REACT_APP_SERVER_URL;

  // Fetch test data to check isManualEntryQuestions
  useEffect(() => {
    const fetchTestData = async () => {
      try {
        const res = await fetchWithAuth(`${serverUrl}/api/test/get/${testId}?onlyInfo=true`, {
          credentials: "include",
        });
        if (!res || !res.ok) return;
        const data = await res.json();
        setTestData(data);
      } catch (err) {
        console.error("Failed to fetch test data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTestData();
  }, [testId, serverUrl]);

  // Fetch institute info to pass to subcomponents and avoid duplicate calls
  useEffect(() => {
    const fetchInstituteData = async () => {
      if (!user?.instituteId) {
        setLoadingInstitute(false);
        return;
      }
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`,
          { credentials: "include" },
        );
        if (!response || !response.ok) {
          throw new Error("Failed to fetch institute details");
        }
        const data = await response.json();
        setInstituteInfo(data);
      } catch (err) {
        console.error("Failed to fetch institute details:", err);
      } finally {
        setLoadingInstitute(false);
      }
    };

    fetchInstituteData();
  }, [user?.instituteId, serverUrl]);

  // Fetch submitted assessments count
  useEffect(() => {
    const fetchSubmittedCount = async () => {
      try {
        const res = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/count/${testId}`,
          {
            credentials: "include",
          }
        );
        if (!res || !res.ok) return;
        const data = await res.json();
        setSubmittedCount(typeof data.count === "number" ? data.count : (Array.isArray(data) ? data.length : 0));
      } catch (err) {
        console.error("Failed to fetch submitted assessments count:", err);
        setSubmittedCount(0);
      }
    };
    fetchSubmittedCount();
  }, [testId, serverUrl]);

  // Simulate fetching class information based on test ID
  useEffect(() => {
    fetchWithAuth(`${serverUrl}/api/breadcrumbs/get?testId=${testId}`, {
      credentials: "include",
    })
      .then((res) => {
        if (!res) return;
        return res.json();
      })
      .then((data) => {
        if (data.class.id) {
          setClassInfo({
            name: data.class.name,
            id: data.class.id,
            instructorName: data.class.instructorName,
            level: data.class.level,
          });
        } else {
          setClassInfo({
            id: null,
            name: "Unknown Class",
            instructorName: "",
            level: "",
          });
        }
        if (data.test.id) {
          setTestInfo({ name: data.test.name, id: data.test.id });
        } else {
          setTestInfo({ id: null, name: "Unknown Test" });
        }
      })
      .catch((err) => {
        setClassInfo({
          id: null,
          name: "Unknown Class",
          instructorName: "",
          level: "",
        });
      });
  }, [testId, serverUrl]);

  const isK12ManualTest = testData?.isManualEntryQuestions && instituteInfo?.category === "K12_SCHOOL";
  const isScoreMismatch = Boolean(
    location.state?.scoreMismatch ||
    location.state?.openSpecialInstructions ||
    new URLSearchParams(location.search).get("scoreMismatch") === "true"
  );

  // Redirect to pending if no submitted assessments (only on first visit),
  // but stay on Submitted Assessments if there is a score mismatch to review
  useEffect(() => {
    if (isScoreMismatch) {
      hasRedirectedRef.current = true;
      return;
    }
    if (
      submittedCount !== null &&
      submittedCount <= 0 &&
      location.pathname === `/instructor/test-grading/${testId}` &&
      !hasRedirectedRef.current &&
      !isK12ManualTest
    ) {
      hasRedirectedRef.current = true;
      navigate(`/instructor/test-grading/${testId}/pending`, { replace: true });
    }
  }, [submittedCount, location.pathname, testId, navigate, testData, isK12ManualTest, isScoreMismatch]);

  // This is test grading Page
  if (!isAuthenticated || primaryRole !== "instructor") {
    return <Navigate to="/login" replace />;
  }

  if (loading || submittedCount === null || loadingInstitute) {
    return (
      <>
        <Navbar links={[]} breadcrumbs={[]} />
        <Loader />
      </>
    );
  }

  // Determine the current page based on the URL
  const pathParts = location.pathname.split("/");
  const currentPath = pathParts[pathParts.length - 1];
  let currentPage = "Submitted Assessments";

  switch (currentPath) {
    case "submitted":
      currentPage = "Submitted Assessments";
      break;
    case "pending":
      currentPage = "Pending Submissions";
      break;
    default:
      currentPage = "Submitted Assessments";
  }

  // Navigation links for the test grading tabs
  const navLinks = [
    {
      text: "Submitted Assessments",
      to: `/instructor/test-grading/${testId}`,
      end: true,
      id: "submitted",
      icon: "fa-solid fa-check-to-slot",
    },
  ];

  // Add Pending Submissions link only when test is not manual
  if (!isK12ManualTest) {
    navLinks.push({
      text: "Pending Submissions",
      to: `/instructor/test-grading/${testId}/pending`,
      id: "pending",
      icon: "fa-solid fa-hourglass-start",
    });
  }

  // Breadcrumbs representing the current navigation hierarchy
  const breadcrumbs = [
    { name: "Home", path: "/instructor", id: "home" },
    {
      name: `${classInfo.name}`,
      path: `/instructor/manage-class/${classInfo.id}`,
      id: "class",
    },
    {
      name: `${testInfo.name}`,
      path: `/instructor/test-grading/${testInfo.id}`,
      isLast: false,
      id: "test",
    },
    { name: currentPage, path: ``, isLast: true, id: "current" },
  ];

  return (
    <>
      <Navbar links={navLinks} breadcrumbs={breadcrumbs} />
      <div className="test-grading-content">
        <Routes>
          <Route
            index
            element={
              <SubmittedAssessments
                testId={testId}
                classInfo={classInfo}
                testInfo={testInfo}
                isManualEntryTest={
                  testData?.manualEntryQuestions?.length > 0 || false
                }
                isOnlineExamination={
                  testData?.isOnlineExamination === true
                }
                isHomeworkTest={
                  Boolean(testData?.isHomeworkTest ?? testData?.isHomework)
                }
                initialInstituteInfo={instituteInfo}
                autoOpenSpecialInstructions={isScoreMismatch}
                scoreMismatchDetails={
                  location.state?.scoreMismatchDetails || {
                    promptMaxScore: location.state?.promptMaxScore,
                    testMaxScore: location.state?.testMaxScore,
                    initialUserPrompt: location.state?.initialUserPrompt,
                  }
                }
              />
            }
          />
          <Route
            path="pending"
            element={
              isK12ManualTest ? (
                <Navigate to={`/instructor/test-grading/${testId}`} replace />
              ) : (
                <PendingAssessments
                  testId={testId}
                  isManualEntryTest={testData?.manualEntryQuestions?.length > 0 || false}
                  isOnlineExamination={
                    testData?.isOnlineExamination === true
                  }
                  initialInstituteInfo={instituteInfo}
                />
              )
            }
          />
          <Route
            path="*"
            element={
              <Navigate to={`/instructor/test-grading/${testId}`} replace />
            }
          />
        </Routes>
      </div>
    </>
  );
}